import {
    Action,
    IAgentRuntime,
    Memory,
    State,
    HandlerCallback,
    elizaLogger,
} from "@elizaos/core";
import { initCotiWalletProvider } from "../providers/wallet";
import { TransferNativeParams, TransferNativeSchema } from "../types/index";
import { ethers } from "ethers";

export const transferNativeAction: Action = {
    name: "TRANSFER_NATIVE",
    similes: [
        "SEND_COTI",
        "TRANSFER_COTI",
        "SEND_TOKENS",
        "TRANSFER_TOKENS",
        "SEND_NATIVE"
    ],
    description: "Transfer native COTI tokens to another address on the COTI blockchain",
    validate: async (runtime: IAgentRuntime, message: Memory): Promise<boolean> => {
        try {
            // Check if message content has the required fields
            if (message.content?.to && message.content?.amount) {
                const params = TransferNativeSchema.parse(message.content);
                return ethers.utils.isAddress(params.to) && parseFloat(params.amount) > 0;
            }

            // Check if message text contains transfer information
            const text = message.content?.text || "";
            const transferMatch = text.match(/(?:send|transfer)\s+(\d+(?:\.\d+)?)\s+(?:coti\s+)?to\s+(0x[a-fA-F0-9]{40})/i);
            
            if (transferMatch) {
                const amount = transferMatch[1];
                const address = transferMatch[2];
                return ethers.utils.isAddress(address) && parseFloat(amount) > 0;
            }

            return false;
        } catch {
            return false;
        }
    },
    handler: async (
        runtime: IAgentRuntime,
        message: Memory,
        state: State | undefined,
        options: any = {},
        callback?: HandlerCallback
    ) => {
        try {
            elizaLogger.info("Starting TRANSFER_NATIVE action");

            let to: string;
            let amount: string;

            // Try to get parameters from message content
            if (message.content?.to && message.content?.amount) {
                const params = TransferNativeSchema.parse(message.content);
                to = params.to;
                amount = params.amount;
            } else {
                // Try to extract from message text
                const text = message.content?.text || "";
                const transferMatch = text.match(/(?:send|transfer)\s+(\d+(?:\.\d+)?)\s+(?:coti\s+)?to\s+(0x[a-fA-F0-9]{40})/i);
                
                if (!transferMatch) {
                    throw new Error("Could not parse transfer parameters from message");
                }
                
                amount = transferMatch[1];
                to = transferMatch[2];
            }

            if (!ethers.utils.isAddress(to)) {
                throw new Error("Invalid recipient address");
            }

            if (parseFloat(amount) <= 0) {
                throw new Error("Amount must be greater than 0");
            }

            const walletProvider = initCotiWalletProvider(runtime);
            const fromAddress = walletProvider.getAddress();

            // Check balance before transfer
            const balance = await walletProvider.getBalance();
            const amountWei = ethers.utils.parseEther(amount);
            
            if (balance.lt(amountWei)) {
                throw new Error(`Insufficient balance. Available: ${ethers.utils.formatEther(balance)} COTI, Required: ${amount} COTI`);
            }

            // Estimate gas
            const estimatedGas = await walletProvider.estimateGas(to, amount);
            elizaLogger.info(`Estimated gas: ${estimatedGas.toString()}`);

            // Execute transfer
            const tx = await walletProvider.transfer(to, amount);
            
            const responseText = `Successfully transferred ${amount} COTI to ${to}. Transaction hash: ${tx.hash}`;

            if (callback) {
                await callback({
                    text: responseText,
                    content: {
                        success: true,
                        transactionHash: tx.hash,
                        from: fromAddress,
                        to,
                        amount,
                    },
                });
            }

            return {
                text: responseText,
                success: true,
                data: {
                    transactionHash: tx.hash,
                    from: fromAddress,
                    to,
                    amount,
                    amountWei: amountWei.toString(),
                    estimatedGas: estimatedGas.toString(),
                },
            };
        } catch (error) {
            elizaLogger.error("Error in TRANSFER_NATIVE action:", error);
            const errorMessage = `Failed to transfer COTI: ${error instanceof Error ? error.message : String(error)}`;

            if (callback) {
                await callback({
                    text: errorMessage,
                    content: { success: false, error: errorMessage },
                });
            }

            return {
                text: errorMessage,
                success: false,
                error: error instanceof Error ? error : new Error(String(error)),
            };
        }
    },
    examples: [
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Send 10 COTI to 0x1234567890123456789012345678901234567890",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll transfer 10 COTI tokens to that address for you.",
                    action: "TRANSFER_NATIVE",
                },
            },
        ],
        [
            {
                name: "{{user1}}",
                content: {
                    text: "Transfer 5.5 COTI to alice",
                },
            },
            {
                name: "{{user2}}",
                content: {
                    text: "I'll send 5.5 COTI tokens to alice's address.",
                    action: "TRANSFER_NATIVE",
                },
            },
        ],
    ],
};
